// Negociação configurável (specs/21): configuração da empresa, tipos e modelos prontos.
import type { PedeValor } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface EtapaCfg {
  id: string; nome: string; ordem: number; cor: string; pedeQuantidade: boolean; rotuloQuantidade: string | null;
  pedeValor: PedeValor; rotuloValor: string | null; prazoDias: number | null; somaPipeline: boolean;
}
export interface ConfigNegociacao {
  ativa: boolean; etapas: EtapaCfg[]; ganho: { id: string; nome: string } | null; perdido: { id: string; nome: string } | null;
  motivos: Array<{ id: string; nome: string }>;
}
export interface NegociacaoCliente {
  id: string; titulo: string; valor: string; quantidade: number | null; status: 'ABERTA' | 'GANHA' | 'PERDIDA'; etapa: string; etapaId: string;
  prazoEm: string | null; motivoPerda: string | null; fechadaEm: string | null; createdAt: string; campanha: string | null;
  /** A quem a negociação conta (specs/27): o vendedor só move as dele; as dos colegas são só consulta */
  responsavelId?: string | null; responsavel?: string | null;
}

/** A negociação é de outro vendedor (para quem não é administrador): só consulta. */
export const deOutro = (n: NegociacaoCliente, meuId: string | undefined, veTodas: boolean) =>
  !veTodas && !!n.responsavelId && n.responsavelId !== meuId;

/** Ao trocar os vendedores do cliente: abertas de quem deixa de atender passam para o principal (specs/27). */
export function abertasQueMudam(negs: NegociacaoCliente[], principal: string | null, adicionais: string[]) {
  if (!principal) return [];
  return negs.filter((o) => o.status === 'ABERTA' && !!o.responsavelId && o.responsavelId !== principal && !adicionais.includes(o.responsavelId));
}

export const useConfigNegociacao = (habilitado = true) =>
  useQuery({ queryKey: ['config-negociacao'], queryFn: () => api<ConfigNegociacao>('/config/negociacao'), staleTime: 60_000, enabled: habilitado });

export type EtapaForm = {
  id?: string; nome: string; pedeQuantidade: boolean; rotuloQuantidade: string; pedeValor: PedeValor; rotuloValor: string;
  prazoDias: string; somaPipeline: boolean;
};
const E = (nome: string, q: string | null, pedeValor: PedeValor, rotuloValor: string, prazo: number | null, pipe: boolean): EtapaForm => ({
  nome, pedeQuantidade: !!q, rotuloQuantidade: q ?? '', pedeValor, rotuloValor, prazoDias: prazo ? String(prazo) : '', somaPipeline: pipe,
});
export const MODELOS: Record<string, { nome: string; desc: string; etapas: () => EtapaForm[] }> = {
  simples: { nome: 'Venda simples', desc: 'Orçamento → Ganho / Perdido', etapas: () => [E('Orçamento', null, 'OBRIGATORIO', 'Valor do orçamento', 15, true)] },
  etiquetas: {
    nome: 'Etiquetas / confecção', desc: 'Amostra (quantidade) → Orçamento (valor) → Ganho / Perdido',
    etapas: () => [E('Amostra', 'Peças enviadas', 'NAO', '', 7, false), E('Orçamento', null, 'OBRIGATORIO', 'Valor do orçamento', 15, true)],
  },
  servicos: {
    nome: 'Serviços', desc: 'Visita técnica → Proposta → Contrato → Ganho / Perdido',
    etapas: () => [E('Visita técnica', null, 'NAO', '', 5, false), E('Proposta', null, 'OBRIGATORIO', 'Valor da proposta', 10, true), E('Contrato', null, 'OBRIGATORIO', 'Valor do contrato', 7, true)],
  },
  personalizado: { nome: 'Personalizado', desc: 'Começa com uma etapa em branco', etapas: () => [E('Nova etapa', null, 'OPCIONAL', 'Valor', 7, true)] },
};

/** Fundo escuro derivado da cor da etapa (texto na própria cor, contraste ok no tema escuro). */
export const fundoEtapa = (cor: string) => `${cor}26`;
export const COR_GANHO = '#86efac';
export const COR_PERDIDO = '#fca5a5';
